import { useEffect, useMemo, useState, useCallback, useRef, useId } from "react";
import type { ReactNode } from "react";
import { createPortal } from "react-dom";
import { Capacitor } from "@capacitor/core";
import { useQuery } from "convex/react";
import { AnimatePresence, motion, useReducedMotion } from "motion/react";
import { Check, Info, LayoutGrid, Search, Tag, X } from "lucide-react";

import { AppIcon } from "../../components/apps/AppIcon";
import { appCategoryDisplayLabel, localizedCategoryLabel } from "../../lib/apps/categoryIcons";
import { resolveCanonicalCategoryKey } from "../../lib/apps/categoryNormalization";
import { assignBrandAppColors } from "../../lib/apps/appUsageColors";
import { AppFriendlyName } from "../../lib/apps/AppFriendlyName";
import { matchesAppSearchQuery } from "../../lib/apps/matchesAppSearchQuery";
import { queryRef } from "../../lib/convex/refs";
import {
  TRACKED_SOFT_CAP,
  TRACKED_SOFT_CAP_WARN_AT,
  type ViewMode,
} from "../../lib/habits/heatmap";
import { isUnattributedHeatmapEntity } from "../../lib/habits/unattributed";
import { BottomSheet } from "./BottomSheet";
import { useScreen0Language } from "@/features/language";
import { formatTemplate, getTranslations } from "@/i18n";

const HABITS_FILTER_APP_ICON_SIZE = 18;
const HABITS_FILTER_APP_ICON_RENDER_SIZE = 27;
const POPOVER_SPRING = { type: "spring" as const, stiffness: 520, damping: 36, mass: 0.7 };

export type FilterEntity = {
  key: string; // namespaced, e.g. "app:com.foo" / "cat:Social"
  name: string;
  displayName: string;
  appIdentifier?: string;
  category?: string;
  isCategory?: boolean;
  color: string;
};

type SearchApp = {
  key: string;
  displayName: string;
  appIdentifier?: string;
  category?: string;
};

type AppFilterSheetProps = {
  open: boolean;
  onClose: () => void;
  viewMode: ViewMode;
  onViewModeChange: (mode: ViewMode) => void;
  anonymousDeviceId: string;
  heatmapStartDate: string;
  heatmapEndDate: string;
  entities: FilterEntity[];
  tracked: string[];
  onChange: (next: string[]) => void;
  includeOther: boolean;
  onIncludeOtherChange: (next: boolean) => void;
};

const VIEW_OPTION_VALUES: ReadonlyArray<{ value: ViewMode; icon: typeof LayoutGrid }> = [
  { value: "app", icon: LayoutGrid },
  { value: "category", icon: Tag },
];

const searchUsedAppsRef = queryRef<
  { anonymousDeviceId: string; startDate: string; endDate: string; query?: string },
  SearchApp[]
>("habitAppSearch:searchUsedApps");

export function AppFilterSheet({
  open,
  onClose,
  viewMode,
  onViewModeChange,
  anonymousDeviceId,
  heatmapStartDate,
  heatmapEndDate,
  entities,
  tracked,
  onChange,
  includeOther,
  onIncludeOtherChange,
}: AppFilterSheetProps) {
  const { languageCode } = useScreen0Language();
  const t = getTranslations(languageCode);
  const viewOptions = VIEW_OPTION_VALUES.map((option) => ({
    ...option,
    label: option.value === "app" ? t.today.viewApps : t.today.viewCategories,
  }));
  const [searchQuery, setSearchQuery] = useState("");
  const [otherInfoOpen, setOtherInfoOpen] = useState(false);

  useEffect(() => {
    if (!open) {
      setSearchQuery("");
      setOtherInfoOpen(false);
    }
  }, [open]);

  const prefix = viewMode === "app" ? "app:" : "cat:";
  const selected = new Set(tracked.filter((key) => key.startsWith(prefix)));
  const allShown = selected.size === 0;
  const trimmedSearch = searchQuery.trim();

  const searchRaw = useQuery(
    searchUsedAppsRef,
    open && viewMode === "app" && trimmedSearch.length > 0
      ? {
          anonymousDeviceId,
          startDate: heatmapStartDate,
          endDate: heatmapEndDate,
          query: trimmedSearch,
        }
      : "skip",
  );

  const entityKeys = useMemo(() => new Set(entities.map((entity) => entity.key)), [entities]);

  const localMatches = useMemo(
    () =>
      trimmedSearch.length > 0
        ? entities
            .filter((entity) => matchesAppSearchQuery(entity, trimmedSearch))
            .filter((entity) => includeOther || !isUnattributedHeatmapEntity(entity))
        : [],
    [entities, trimmedSearch, includeOther],
  );

  const historyMatches = useMemo<FilterEntity[]>(() => {
    if (!searchRaw || searchRaw.length === 0) return [];
    const filtered = searchRaw.filter((app) => !entityKeys.has(app.key));
    const colored = assignBrandAppColors(
      filtered.map((app) => ({
        appIdentifier: app.appIdentifier,
        displayName: app.displayName,
        category: app.category,
      })),
      undefined,
      filtered.length,
    );
    return filtered
      .filter((app) => includeOther || !isUnattributedHeatmapEntity(app))
      .map((app, index) => {
      const withColor = colored[index] as { color?: string } | undefined;
      return {
        key: app.key,
        name: app.displayName,
        displayName: app.displayName,
        appIdentifier: app.appIdentifier,
        category: app.category,
        isCategory: false,
        color: withColor?.color ?? "#7ab89a",
      };
    });
  }, [entityKeys, searchRaw, includeOther]);

  const toggle = (key: string) => {
    if (selected.has(key)) {
      onChange(tracked.filter((item) => item !== key));
      return;
    }
    if (selected.size >= TRACKED_SOFT_CAP) return;
    onChange([...tracked, key]);
  };

  const showAll = () => onChange(tracked.filter((key) => !key.startsWith(prefix)));

  const atSoftCap = selected.size >= TRACKED_SOFT_CAP;
  const nearSoftCap = selected.size >= TRACKED_SOFT_CAP_WARN_AT && !atSoftCap;

  return (
    <BottomSheet open={open} onClose={onClose} labelledBy="habit-filter-title">
      <div className="flex items-center justify-between px-4 pt-4 pb-1">
        <h2 id="habit-filter-title" className="text-lg font-semibold text-[oklch(92%_0.02_150)]">
          {t.habits.filterTitle}
        </h2>
        <button
          type="button"
          onClick={onClose}
          className="icon-button"
          style={{ minWidth: 36, minHeight: 36, border: "none" }}
          aria-label={t.habits.closeFilter}
        >
          <X size={18} />
        </button>
      </div>

      <div className="max-h-[62svh] overflow-y-auto px-2 pb-2">
        <FilterSection label={t.habits.filterView}>
          {viewOptions.map((option) => (
            <FilterRadioRow
              key={option.value}
              label={option.label}
              icon={option.icon}
              selected={viewMode === option.value}
              onSelect={() => onViewModeChange(option.value)}
              group="habit-view"
            />
          ))}
        </FilterSection>

        {viewMode === "app" ? (
          <FilterSection label={t.habits.filterOtherSection} className="mt-1">
            <IncludeOtherRow
              enabled={includeOther}
              onChange={onIncludeOtherChange}
              onInfo={() => setOtherInfoOpen(true)}
            />
          </FilterSection>
        ) : null}

        <FilterSection label={viewMode === "app" ? t.habits.filterTrackApps : t.habits.filterTrackCategories} className="mt-1">
          <p className="px-2 pb-1 text-xs text-[oklch(58%_0.025_155)]">
            {allShown
              ? t.habits.filterShowingAll
              : formatTemplate(t.habits.filterTrackingSelected, { count: selected.size })}
          </p>
          {nearSoftCap ? (
            <p className="px-2 pb-1 text-xs text-[oklch(72%_0.07_85)]">
              {formatTemplate(t.habits.filterTrackLimitNear, {
                cap: TRACKED_SOFT_CAP,
                slots: TRACKED_SOFT_CAP - selected.size,
              })}
            </p>
          ) : null}
          {atSoftCap ? (
            <p className="px-2 pb-1 text-xs text-[oklch(72%_0.07_85)]">
              {formatTemplate(t.habits.filterTrackLimitReached, { cap: TRACKED_SOFT_CAP })}
            </p>
          ) : null}

          {viewMode === "app" ? (
            <div className="px-2 pb-2">
              <label className="liquid-glass-input-shell relative flex items-center gap-2 px-3 py-2.5">
                <Search size={15} className="shrink-0 text-[oklch(58%_0.025_155)]" aria-hidden />
                <input
                  type="search"
                  value={searchQuery}
                  onChange={(event) => setSearchQuery(event.target.value)}
                  placeholder={t.habits.filterSearchPlaceholder}
                  className="min-h-11 min-w-0 flex-1 bg-transparent text-base text-[oklch(88%_0.02_150)] outline-none placeholder:text-[oklch(52%_0.025_155)]"
                  aria-label={t.habits.filterSearchAria}
                />
              </label>
            </div>
          ) : null}

          {viewMode === "app" && trimmedSearch.length > 0 ? (
            <div className="pb-1">
              <p className="px-2 pb-1 text-[11px] font-semibold uppercase tracking-wide text-[oklch(52%_0.03_155)]">
                {t.habits.filterSearchResults}
              </p>
              {localMatches.map((entity) => (
                <TrackRow
                  key={entity.key}
                  entity={entity}
                  checked={selected.has(entity.key)}
                  disabled={!selected.has(entity.key) && atSoftCap}
                  onToggle={() => toggle(entity.key)}
                />
              ))}

              {searchRaw === undefined ? (
                <p className="px-2 py-2 text-sm text-[oklch(58%_0.025_155)]">{t.habits.filterSearching}</p>
              ) : historyMatches.length > 0 ? (
                <>
                  <p className="px-2 pb-1 pt-2 text-[11px] font-semibold uppercase tracking-wide text-[oklch(52%_0.03_155)]">
                    {t.habits.filterSearchHistoryApps}
                  </p>
                  {historyMatches.map((entity) => (
                    <TrackRow
                      key={entity.key}
                      entity={entity}
                      checked={selected.has(entity.key)}
                      disabled={!selected.has(entity.key) && atSoftCap}
                      onToggle={() => toggle(entity.key)}
                    />
                  ))}
                </>
              ) : null}

              {searchRaw !== undefined &&
              localMatches.length === 0 &&
              historyMatches.length === 0 ? (
                <p className="px-2 py-2 text-sm text-[oklch(58%_0.025_155)]">{t.habits.filterNoMatchingApps}</p>
              ) : null}
            </div>
          ) : entities.length === 0 ? (
            <p className="px-2 py-3 text-sm text-[oklch(58%_0.025_155)]">{t.habits.filterNoItemsToTrack}</p>
          ) : (
            entities.map((entity) => (
              <TrackRow
                key={entity.key}
                entity={entity}
                checked={selected.has(entity.key)}
                disabled={!selected.has(entity.key) && atSoftCap}
                onToggle={() => toggle(entity.key)}
              />
            ))
          )}
        </FilterSection>
      </div>

      <OtherBucketInfoDialog open={otherInfoOpen} onClose={() => setOtherInfoOpen(false)} />

      <div className="liquid-glass-sheet-footer flex gap-2 px-4 pt-3">
        <button type="button" onClick={showAll} className="secondary-action flex-1" disabled={allShown}>
          {t.habits.filterShowAll}
        </button>
        <button type="button" onClick={onClose} className="primary-action flex-1">
          {t.common.done}
        </button>
      </div>
    </BottomSheet>
  );
}

function IncludeOtherRow({
  enabled,
  onChange,
  onInfo,
}: {
  enabled: boolean;
  onChange: (next: boolean) => void;
  onInfo: () => void;
}) {
  const { languageCode } = useScreen0Language();
  const t = getTranslations(languageCode);

  return (
    <div className="flex items-center justify-between gap-3 rounded-lg px-2 py-2.5">
      <div className="flex min-w-0 flex-1 items-center gap-1.5">
        <span className="truncate text-sm font-medium text-[oklch(88%_0.02_150)]">
          {t.habits.includeOther}
        </span>
        <button
          type="button"
          onClick={onInfo}
          className="liquid-glass-icon-ring inline-flex size-[18px] shrink-0 items-center justify-center text-[oklch(58%_0.03_155)] transition-colors duration-150 hover:text-[oklch(72%_0.03_155)] active:scale-95"
          aria-label={t.habits.includeOtherInfoAria}
        >
          <Info size={11} strokeWidth={2.5} aria-hidden />
        </button>
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={enabled}
        aria-label={t.habits.includeOtherAria}
        className={`relative h-8 w-14 shrink-0 rounded-full transition-colors ${enabled ? "bg-[oklch(72%_0.09_155)]" : "bg-[oklch(32%_0.02_155)]"}`}
        onClick={() => onChange(!enabled)}
      >
        <span
          className={`absolute top-1 left-1 h-6 w-6 rounded-full bg-white transition-transform ${enabled ? "translate-x-6" : "translate-x-0"}`}
        />
      </button>
    </div>
  );
}

function OtherBucketInfoDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { languageCode } = useScreen0Language();
  const t = getTranslations(languageCode);
  const prefersReducedMotion = useReducedMotion();
  const dismissRef = useRef<HTMLButtonElement>(null);
  const dialogId = useId();

  const close = useCallback(() => {
    onClose();
  }, [onClose]);

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.stopPropagation();
        close();
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [open, close]);

  useEffect(() => {
    if (open) dismissRef.current?.focus();
  }, [open]);

  const centeredTransform = { x: "-50%", y: "-50%" } as const;

  return typeof document !== "undefined"
    ? createPortal(
        <AnimatePresence>
          {open ? (
            <>
              <div className="fixed inset-0 z-[60]" aria-hidden="true" onPointerDown={close} />
              <motion.div
                id={dialogId}
                role="dialog"
                aria-label={t.habits.otherInfoTitle}
                className="liquid-glass-pill fixed left-1/2 top-1/2 z-[70] w-[min(18rem,calc(100vw-2rem))] overflow-hidden rounded-xl p-2.5"
                style={{ transformOrigin: "center center" }}
                initial={
                  prefersReducedMotion
                    ? { opacity: 0, ...centeredTransform }
                    : { opacity: 0, scale: 0.96, ...centeredTransform }
                }
                animate={{ opacity: 1, scale: 1, ...centeredTransform }}
                exit={
                  prefersReducedMotion
                    ? { opacity: 0, ...centeredTransform }
                    : { opacity: 0, scale: 0.96, ...centeredTransform }
                }
                transition={prefersReducedMotion ? { duration: 0 } : POPOVER_SPRING}
                onPointerDown={(event) => event.stopPropagation()}
              >
                <h3 className="px-1 pb-1 text-sm font-semibold text-[oklch(92%_0.02_150)]">
                  {t.habits.otherInfoTitle}
                </h3>
                <p className="px-1 pb-2 text-sm leading-6 text-[oklch(72%_0.025_155)]">
                  {Capacitor.getPlatform() === "android"
                    ? t.habits.otherInfoBodyAndroid
                    : t.habits.otherInfoBody}
                </p>
                <div className="flex items-center justify-end">
                  <button
                    ref={dismissRef}
                    type="button"
                    className="min-h-[44px] rounded-lg px-3 text-sm font-semibold text-[oklch(74%_0.025_155)] transition-colors active:bg-[oklch(24%_0.025_165)]"
                    onClick={close}
                  >
                    {t.common.close}
                  </button>
                </div>
              </motion.div>
            </>
          ) : null}
        </AnimatePresence>,
        document.body,
      )
    : null;
}

function FilterSection({
  label,
  children,
  className = "",
}: {
  label: string;
  children: ReactNode;
  className?: string;
}) {
  return (
    <section className={className}>
      <h3 className="px-2 pb-1 pt-2 text-[11px] font-semibold uppercase tracking-wide text-[oklch(52%_0.03_155)]">
        {label}
      </h3>
      <div className="liquid-glass-inset p-1">
        {children}
      </div>
    </section>
  );
}

function FilterRadioRow({
  label,
  icon: Icon,
  selected,
  onSelect,
  group,
}: {
  label: string;
  icon: typeof LayoutGrid;
  selected: boolean;
  onSelect: () => void;
  group: string;
}) {
  const prefersReducedMotion = useReducedMotion();

  return (
    <button
      type="button"
      onClick={onSelect}
      className={`relative flex w-full items-center gap-3 rounded-lg px-2 py-2.5 text-left transition-colors active:scale-[0.99] ${
        selected ? "text-[oklch(92%_0.02_150)]" : "text-[oklch(72%_0.025_155)]"
      }`}
      aria-pressed={selected}
    >
      {selected ? (
        <motion.span
          layoutId={`${group}-active`}
          aria-hidden
          className="absolute inset-0 rounded-lg bg-[oklch(20%_0.025_165)]"
          transition={
            prefersReducedMotion ? { duration: 0 } : { type: "spring", stiffness: 480, damping: 38 }
          }
        />
      ) : null}
      <Icon size={16} className="relative shrink-0 opacity-80" />
      <span className="relative min-w-0 flex-1 text-sm font-medium">{label}</span>
      {selected ? (
        <Check size={15} className="relative shrink-0 text-[oklch(72%_0.09_155)]" />
      ) : (
        <span className="relative h-4 w-4 shrink-0" aria-hidden />
      )}
    </button>
  );
}

function TrackRow({
  entity,
  checked,
  disabled,
  onToggle,
}: {
  entity: FilterEntity;
  checked: boolean;
  disabled: boolean;
  onToggle: () => void;
}) {
  const t = getTranslations(useScreen0Language().languageCode);
  const isOther = isUnattributedHeatmapEntity(entity);
  return (
    <button
      type="button"
      onClick={onToggle}
      disabled={disabled}
      className="flex w-full items-center gap-3 rounded-xl px-2 py-2.5 text-left transition-colors active:bg-[oklch(20%_0.02_165)] disabled:opacity-50"
      aria-pressed={checked}
    >
      <AppIcon
        displayName={entity.displayName}
        appIdentifier={entity.appIdentifier}
        category={entity.category}
        isCategory={entity.isCategory}
        color={entity.color}
        size={HABITS_FILTER_APP_ICON_SIZE}
        iconSize={HABITS_FILTER_APP_ICON_RENDER_SIZE}
      />
      <div className="min-w-0 flex-1">
        <div className="truncate text-sm text-[oklch(88%_0.02_150)]">
          {entity.isCategory ? (
            localizedCategoryLabel(
              resolveCanonicalCategoryKey(entity.category ?? entity.displayName),
              t,
            )
          ) : isOther ? (
            t.categories.other
          ) : (
            <AppFriendlyName
              displayName={entity.displayName}
              appIdentifier={entity.appIdentifier}
              category={entity.category}
            />
          )}
        </div>
        {!entity.isCategory ? (
          <div className="truncate text-xs text-[oklch(72%_0.025_155)]">
            {appCategoryDisplayLabel(entity.category, t, entity.appIdentifier)}
          </div>
        ) : null}
      </div>
      <span
        className={`grid h-5 w-5 shrink-0 place-items-center rounded-md ${
          checked
            ? "bg-[oklch(72%_0.09_155)] text-[oklch(12%_0.015_160)]"
            : "liquid-glass-day-inactive text-transparent"
        }`}
      >
        <Check size={13} />
      </span>
    </button>
  );
}
